import * as mobx from "mobx";
import SessionStore from '../../services/session/SessionStore'
import BrandConfigStore from '../../services/config/BrandConfigStore'
import SelectedLocationsStore from '../../services/locations/SelectedLocationsStore'
import {getPathAndQuery} from '../../util/UrlFragmentSearch'
import EventEmitter from 'events'


/**
 * This module modifies the angular application so that it, and ui-router plays nice with being unmounted/mounted.
 *
 * Related article
 * https://medium.com/appifycanada/angular-components-in-react-2c929130f995
 *
 *
 * It also has some compatibility with services that have been migrated off of angular,
 * but still need to be updated, or propagate updates
 */

const events = new EventEmitter()
let isMounted = false

let _hackGroupId = false
/** react component container */
export function unmount() {
  events.emit('beforeunmount')
  isMounted = false
  _hackGroupId  = false
  events.emit('unmount')
}

export function remount({
  hackGroupId = true
} = {}) {
  events.emit('beforemount')
  isMounted = true;
  _hackGroupId = hackGroupId;
  events.emit('mount')
}

/** configures angular application */
export function install(clientDashboardApp) {
  clientDashboardApp.run((
    $rootScope, $state,
    $location
  ) => {
    events.once('beforeunmount', () => {
      // in order that the current state gets unmounted
      // otherwise the previous state will still be mounted and running
      // (e.g. updating its data when the location selector is modified)

      $state.transitionTo('404', {}, {
        location: false,
        reload: false
      })

    })

    // bind some functionality of mobx stores that used to be angular stores.
    // stop reacting when angular is unmounted (this function gets re-run whenever angular is mounted)
    // angular controllers depend on a global user for notes
    events.once('unmount', mobx.reaction(
      () => SessionStore.user,
      (user) => $rootScope.userInfo = mobx.toJS(user),
      { fireImmediately: true }
    ))

    // propagate location changes to ui-router (GroupDropdownService used to be doing this)
    events.once('unmount', mobx.reaction(
      () => SelectedLocationsStore.legacyGroupIdOrAll,
      groupId => {
        // this makes transition angular components that don't actually use ui-router wig out
        // (ends up resetting the url to previous values when selected locations are modified)
        if (_hackGroupId) {
          $state.go($state.current, { groupId })
        }
      }
    ))

    // initialize selected locations store from angular's ui-router's groupId param on first navigation
    const unsubscribe = $rootScope.$on('$stateChangeStart', (event, state, params, ...args) => {
      if (params && params.groupId && params.groupId !== SelectedLocationsStore.legacyGroupId) {
        SelectedLocationsStore.setLegacyGroupId(params.groupId)
      }
      unsubscribe()
    })

    /**
     * Some hacks to make ui-router more friendly with external updates to the url (for compatibility with react router)
     */

      // monkey patch ui-router to disable functions doing anything while not isMounted
    const {
        go,
        reload
      } = $state

    // only execute the given ui-router function if the angular app is mounted
    function onlyIfMounted(fn) {
      return (...args) => {
        if (isMounted) {
          return fn(...args)
        } else {
          // console.info('hijack ui-router ', fn)
        }
      }
    }

    Object.assign($state, {
      go: onlyIfMounted(go),
      reload: onlyIfMounted((...args) => {
        //
        // rechecks the current state against the url before reloading:
        // prevents ui-router from reloading previous states that it hasn't tracked yet due to an external location change
        // (e.g. from navigating via react router, or just setting the window.location)
        // without this, ui-router forcefully goes back to the previous page (from `$timeout(() => $state.$reload())`)

        const statePaths = $state.getCurrentPath();
        const lastStatePath = statePaths[statePaths.length - 1];

        const currentPath = getPathAndQuery().pathname;
        const didMatch = lastStatePath.state.url.exec(currentPath)

        if (!!didMatch) {
          return reload(...args)
        }
      })
    })


    /** this is very important, otherwise angular starts leaking controllers */
    events.once('unmount', () => $rootScope.$destroy())

  })

  clientDashboardApp.config(function ($httpProvider, $uibTooltipProvider, $provide) {

    // https://medium.com/appifycanada/angular-components-in-react-2c929130f995
    // prevent angular from controlling the url
    $provide.decorator('$browser', ['$delegate', function ($delegate) {
      const {onUrlChange, url} = $delegate
      $delegate.onUrlChange = (...args) => {
        if (isMounted) { // only call when mounted
          return onUrlChange(...args);
        }
      }

      return $delegate;
    }]);
  });


  clientDashboardApp.controller('RootController', ($scope, $timeout) => {
    // compatibility glue for naughty scope things that IndexController used to be doing
    // various directives and controllers expect this to be available on their inherited scopes
    $scope.$on('$destroy', mobx.reaction(
      () => BrandConfigStore.brandConfig,
      (brandConfig) => $timeout(() => {
        $scope.brandConfig = mobx.toJS(brandConfig)
      }, 0),
      { fireImmediately: true }
    ))
  })


  /**
   * emulates some of the functionality of ui-router state declarations via a directive:
   *
   * .state("things", {
   *   controller: "FooControl",
   *   template: "<div>{{scopeValue}}</div>"
   * }
   *
   * https://github.com/angular-ui/ui-router/blob/1.0.3/src/directives/viewDirective.ts#L338
   */
  clientDashboardApp.directive('uiRouterStateGlue', ($compile, $controller, glueContainer) => {
    return {
      restrict: "AE",
      compile(tElement) {
        return (scope, $element) => {
          const cfg = glueContainer.getContainer().props.state;
          $element.html(cfg.template);
          const link = $compile($element.contents());

          const controller = cfg.controller;
          const controllerAs = cfg.controllerAs;

          if (controller) {
            let controllerInstance = $controller(controller, { $scope: scope, $element: $element });
              if (controllerAs) {
                scope[controllerAs] = controllerInstance;
                // scope[controllerAs][resolveAs] = locals;
              }

              $element.data('$ngControllerController', controllerInstance);
              $element.children().data('$ngControllerController', controllerInstance);

              // registerControllerCallbacks($q, $transitions, controllerInstance, scope, cfg);
            }

          link(scope);
        }

      }
    }
  })

}
